import Swal from 'sweetalert2';
import { useAppDownload } from '@/context/AppDownloadContext';

interface AppOption {
  id: string;
  icon: string;
  name: string;
  description: string;
  status: 'available' | 'soon';
  statusLabel: string;
  qrLabel: string;
}

const appCategories: { title: string; subtitle?: string; items: AppOption[] }[] = [
  {
    title: 'Android',
    items: [
      {
        id: 'android-play',
        icon: 'ri-google-play-fill',
        name: 'Google Play',
        description: 'Descarga la app oficial desde la tienda de Google.',
        status: 'available',
        statusLabel: 'Disponible',
        qrLabel: 'QR Google Play',
      },
      {
        id: 'android-apk',
        icon: 'ri-android-fill',
        name: 'APK Directo',
        description: 'Instalación directa del archivo APK para Android.',
        status: 'soon',
        statusLabel: 'Próximamente',
        qrLabel: 'QR APK Directo',
      },
    ],
  },
  {
    title: 'iPhone / iPad',
    items: [
      {
        id: 'ios-store',
        icon: 'ri-app-store-fill',
        name: 'App Store',
        description: 'Descarga la app oficial desde la App Store de Apple.',
        status: 'soon',
        statusLabel: 'Próximamente',
        qrLabel: 'QR App Store',
      },
    ],
  },
  {
    title: 'Smart TV',
    subtitle: 'Escucha radios y mira lives desde tu pantalla.',
    items: [
      {
        id: 'tv-android',
        icon: 'ri-android-line',
        name: 'Android TV',
        description: 'Disponible en Google Play para Android TV.',
        status: 'soon',
        statusLabel: 'Próximamente',
        qrLabel: 'QR Android TV',
      },
      {
        id: 'tv-fire',
        icon: 'ri-amazon-fill',
        name: 'Fire TV',
        description: 'App para dispositivos Amazon Fire TV.',
        status: 'soon',
        statusLabel: 'Próximamente',
        qrLabel: 'QR Fire TV',
      },
      {
        id: 'tv-roku',
        icon: 'ri-tv-line',
        name: 'Roku',
        description: 'Canal disponible en la tienda de Roku.',
        status: 'soon',
        statusLabel: 'Próximamente',
        qrLabel: 'QR Roku',
      },
      {
        id: 'tv-samsung',
        icon: 'ri-smartphone-line',
        name: 'Samsung TV',
        description: 'App para televisores Samsung con Tizen.',
        status: 'soon',
        statusLabel: 'Próximamente',
        qrLabel: 'QR Samsung TV',
      },
      {
        id: 'tv-lg',
        icon: 'ri-tv-2-line',
        name: 'LG TV',
        description: 'App para televisores LG con webOS.',
        status: 'soon',
        statusLabel: 'Próximamente',
        qrLabel: 'QR LG TV',
      },
    ],
  },
  {
    title: 'Radio Individual',
    subtitle: 'App personalizada para estaciones Premium.',
    items: [
      {
        id: 'radio-premium',
        icon: 'ri-radio-line',
        name: 'App Personalizada',
        description: 'App exclusiva con la identidad de tu emisora. Disponible para estaciones con plan Premium.',
        status: 'soon',
        statusLabel: 'Disponible por plan Premium',
        qrLabel: 'QR App Personalizada',
      },
    ],
  },
];

const swalBase = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold text-sm',
    htmlContainer: 'text-xs',
    confirmButton: 'rounded-lg px-5 py-2 text-xs font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-xs font-bold whitespace-nowrap',
  },
  buttonsStyling: false,
});

function simulateDownload(itemName: string, platform: string) {
  swalBase.fire({
    title: 'Preparando descarga',
    html: `<p class="text-foreground-500">Obteniendo <strong>${itemName}</strong> para <strong>${platform}</strong>...</p>`,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });

  setTimeout(() => {
    swalBase.fire({
      icon: 'success',
      title: '¡Descarga lista!',
      html: `<p class="text-foreground-500">La app <strong>${itemName}</strong> para ${platform} se está descargando.</p><p class="text-foreground-400 text-[10px] mt-1">(Simulación — las apps se generarán con Cursor.)</p>`,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#10b981',
      timer: 3000,
      timerProgressBar: true,
    });
  }, 1800);
}

function showComingSoon(itemName: string, platform: string) {
  swalBase.fire({
    icon: 'info',
    title: 'Próximamente',
    html: `<p class="text-foreground-500"><strong>${itemName}</strong> para <strong>${platform}</strong> estará disponible pronto.</p><p class="text-foreground-400 text-[10px] mt-1">Las apps se generarán posteriormente con Cursor.</p>`,
    confirmButtonText: 'Entendido',
    confirmButtonColor: '#f59e0b',
    timer: 3000,
    timerProgressBar: true,
  });
}

function StatusBadge({ status, label }: { status: 'available' | 'soon'; label: string }) {
  if (status === 'available') {
    return (
      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
        {label}
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
      {label}
    </span>
  );
}

function QrPlaceholder({ label }: { label: string }) {
  return (
    <div className="w-16 h-16 rounded-lg bg-background-200/60 flex flex-col items-center justify-center shrink-0">
      <i className="ri-qr-code-line text-foreground-400 text-lg"></i>
      <span className="text-[8px] text-foreground-400 mt-0.5 leading-tight text-center">{label}</span>
    </div>
  );
}

export default function AppDownloadModal() {
  const { isOpen, closeModal } = useAppDownload();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={closeModal}
      ></div>

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] mx-4 bg-background-50 rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-6 py-5 border-b border-background-200 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground-950">Descarga RadioFrecuenciaWeb</h2>
            <p className="text-foreground-500 text-xs mt-1">Elige dónde quieres escuchar tus frecuencias WEB.</p>
          </div>
          <button
            onClick={closeModal}
            className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center shrink-0 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto max-h-[calc(85vh-80px)] space-y-6">
          {appCategories.map((category) => (
            <div key={category.title}>
              <div className="mb-3">
                <h3 className="text-sm font-bold text-foreground-900">{category.title}</h3>
                {category.subtitle && (
                  <p className="text-foreground-400 text-[11px] mt-0.5">{category.subtitle}</p>
                )}
              </div>

              <div className="space-y-2">
                {category.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 p-3 rounded-xl bg-background-100/80 border border-background-200/70 hover:border-background-300 transition-colors"
                  >
                    {/* Icon */}
                    <div className="w-11 h-11 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-accent-600 text-xl`}></i>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="text-sm font-semibold text-foreground-900">{item.name}</h4>
                        <StatusBadge status={item.status} label={item.statusLabel} />
                      </div>
                      <p className="text-foreground-500 text-xs leading-relaxed">{item.description}</p>
                    </div>

                    {/* QR + Button */}
                    <div className="flex items-center gap-3 shrink-0">
                      <QrPlaceholder label={item.qrLabel} />

                      {item.status === 'available' ? (
                        <button
                          onClick={() => simulateDownload(item.name, category.title)}
                          className="px-4 py-2 rounded-lg bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-download-2-line mr-1"></i>
                          Descargar
                        </button>
                      ) : (
                        <button
                          onClick={() => showComingSoon(item.name, category.title)}
                          className="px-4 py-2 rounded-lg bg-foreground-100 hover:bg-foreground-200 text-foreground-500 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-time-line mr-1"></i>
                          Próximamente
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}